// src/services/m3uParser.js
import axios from 'axios';

// Constante para limitar o número de canais durante o desenvolvimento
// Defina como null para processar todos os canais
const DEV_CHANNEL_LIMIT = null; // Exemplo: 500 para limitar a 500 canais

export default class M3UParser {
  constructor() {
    this.channels = [];
    this.groups = new Set();
    this.idCounters = {}; // Para contar ocorrências de IDs repetidos
    this.onProgressCallback = null;
  }

  // Permite definir um callback para acompanhar o progresso do parsing
  setProgressCallback(callback) {
    this.onProgressCallback = callback;
  }

  async loadFromURL(url) {
    try {
      // Reporta o início do download
      this.reportProgress('Iniciando download do arquivo M3U...');
      
      const response = await axios.get(url, {
        responseType: 'text',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            this.reportProgress(`Baixando arquivo M3U: ${percentCompleted}%`);
          } else {
            this.reportProgress(`Baixando arquivo M3U: ${Math.round(progressEvent.loaded / 1024)} KB`);
          }
        }
      });
      
      this.reportProgress('Download concluído. Iniciando processamento...');
      return this.parse(response.data);
    } catch (error) {
      console.error('Erro ao carregar a lista M3U:', error);
      throw error;
    }
  }

  reportProgress(message) {
    console.log(message);
    if (this.onProgressCallback) {
      this.onProgressCallback(message);
    }
  }

  parse(content) {
    if (!content.includes('#EXTM3U')) {
      throw new Error('Formato M3U inválido');
    }

    // Dividir o conteúdo por quebras de linha
    const lines = content.split('\n');
    const totalLines = lines.length;
    
    this.reportProgress(`Arquivo contém ${totalLines} linhas. Iniciando processamento...`);
    
    let currentChannel = {};
    let processedChannels = 0;
    let lastProgressReport = 0;

    this.channels = [];
    this.groups = new Set();
    this.idCounters = {}; // Resetar os contadores

    for (let i = 0; i < lines.length; i++) {
      // Reporta o progresso a cada 5% ou a cada 10000 linhas
      const currentProgress = Math.floor((i / totalLines) * 100);
      if (currentProgress >= lastProgressReport + 5 || i % 10000 === 0) {
        this.reportProgress(`Processando: ${currentProgress}% (${i}/${totalLines} linhas)`);
        lastProgressReport = currentProgress;
      }
      
      const line = lines[i].trim();

      // Ignorar linhas vazias
      if (!line) continue;

      // Se for uma linha de info do canal
      if (line.startsWith('#EXTINF')) {
        currentChannel = this.parseChannelInfo(line);
      } 
      // Se não for uma linha de comentário e for uma URL
      else if (!line.startsWith('#') && (line.startsWith('http') || line.startsWith('rtmp'))) {
        currentChannel.url = line;
        // Apenas adiciona o canal se tiver uma URL
        if (currentChannel.url) {
          // Garante que cada canal tenha um ID único
          if (!currentChannel.id) {
            currentChannel.id = `channel-${this.channels.length}`;
          } else {
            // Se esse ID já foi usado antes, adiciona um sufixo numérico
            if (this.idCounters[currentChannel.id]) {
              this.idCounters[currentChannel.id]++;
              currentChannel.id = `${currentChannel.id}-${this.idCounters[currentChannel.id]}`;
            } else {
              this.idCounters[currentChannel.id] = 1;
            }
          }
          
          // Detecta a qualidade do canal pelo nome
          currentChannel.quality = this.detectQuality(currentChannel.name);
          
          // Limpa o nome do canal removendo tags de qualidade
          currentChannel.cleanName = this.cleanChannelName(currentChannel.name);
          
          this.channels.push({...currentChannel});
          processedChannels++;
          
          // Se estiver no modo de desenvolvimento com limite de canais
          if (DEV_CHANNEL_LIMIT && processedChannels >= DEV_CHANNEL_LIMIT) {
            this.reportProgress(`Limite de ${DEV_CHANNEL_LIMIT} canais atingido (modo de desenvolvimento)`);
            break;
          }
        }
      }
    }

    this.reportProgress(`Processamento concluído. ${this.channels.length} canais encontrados em ${Array.from(this.groups).length} grupos.`);

    return {
      channels: this.channels,
      groups: Array.from(this.groups)
    };
  }

  // Método para processar uma única linha de informação do canal
  parseChannelInfo(line) {
    const channel = {};
    
    // Extrair ID do canal
    const tvgId = line.match(/tvg-id="([^"]*)"/);
    if (tvgId && tvgId[1]) {
      channel.id = tvgId[1];
    }
    
    // Extrair nome do canal
    const tvgName = line.match(/tvg-name="([^"]*)"/);
    if (tvgName && tvgName[1]) {
      channel.name = tvgName[1];
    }
    
    // Extrair logo do canal
    const tvgLogo = line.match(/tvg-logo="([^"]*)"/);
    if (tvgLogo && tvgLogo[1]) {
      channel.logo = tvgLogo[1];
    }
    
    // Extrair grupo do canal
    const groupTitle = line.match(/group-title="([^"]*)"/);
    if (groupTitle && groupTitle[1]) {
      channel.group = groupTitle[1];
      this.groups.add(groupTitle[1]);
    }
    
    // Extrair nome do display (após a última vírgula)
    const displayNameParts = line.split(',');
    if (displayNameParts.length > 1) {
      const displayName = displayNameParts[displayNameParts.length - 1].trim();
      if (displayName && (!channel.name || channel.name === '')) {
        channel.name = displayName;
      }
    }
    
    return channel;
  }

  getChannelsByGroup(group) {
    return this.channels.filter(channel => channel.group === group);
  }

  searchChannels(query) {
    const searchTerm = query.toLowerCase();
    return this.channels.filter(channel => 
      channel.name && channel.name.toLowerCase().includes(searchTerm)
    );
  }

  // Método para limpar o nome do canal removendo tags de qualidade
  cleanChannelName(name) {
    // Remove qualidade entre colchetes como [HD], [SD], [FHD], [4K], [H265], [HDR], etc.
    return name.replace(/\s*\[(HD|SD|FHD|4K|H265|HEVC|UHD|HDR)\]*/gi, '').trim();
  }

  // Método para detectar a qualidade do canal pelo nome
  detectQuality(name) {
    const nameLower = name.toLowerCase();
    
    // Detecta qualidades compostas primeiro
    if (nameLower.includes('fhd') && nameLower.includes('hdr')) return 'FHD HDR';
    if (nameLower.includes('fhd') && nameLower.includes('h265')) return 'FHD H265';
    if (nameLower.includes('4k') && nameLower.includes('hdr')) return '4K HDR';
    if (nameLower.includes('4k') && nameLower.includes('h265')) return '4K H265';
    
    // Depois verifica qualidades simples
    if (nameLower.includes('4k')) return '4K';
    if (nameLower.includes('fhd') || nameLower.includes('fullhd') || nameLower.includes('full hd') || nameLower.includes('1080')) return 'FHD';
    if (nameLower.includes('hd') || nameLower.includes('720')) return 'HD';
    
    return 'SD';
  }
}